import { intervalToDuration, isToday, type Locale } from 'date-fns';
import {
  format,
  formatDistance,
  formatDuration,
  isAfter,
  getDaysInMonth,
  setDate,
  startOfDay,
  sub,
  isPast,
} from 'date-fns';
import { enCA, frCA, es } from 'date-fns/locale';
import { getI18nLanguage } from './language';

export function isDatePast(date: Date) {
  return isPast(date) && !isToday(date);
}

export function isInLast24Hours(date: Date) {
  const oneDayAgo = sub(new Date(), { days: 1 });
  return isAfter(date, oneDayAgo);
}

export function isInLastWeek(date: Date) {
  const oneWeekAgo = sub(new Date(), { days: 7 });
  return isAfter(date, oneWeekAgo);
}

export function isInLastMonth(date: Date) {
  const oneMonthAgo = sub(new Date(), { months: 1 });
  return isAfter(date, oneMonthAgo);
}

function getLocale(): Locale {
  const languagesToLocale = new Map<string, Locale>([
    ['fr', frCA],
    ['en', enCA],
    ['es', es],
  ]);

  return languagesToLocale.get(getI18nLanguage()) ?? frCA;
}

export function localeAwareFormatDistance(date: number | Date, baseDate: number | Date) {
  return formatDistance(date, baseDate, { locale: getLocale() });
}

export function localeAwareFormatDurationShort(date: number | Date) {
  const seconds = Math.abs(Math.floor((new Date().getTime() - (date instanceof Date ? date.getTime() : date)) / 1000));
  return localeAwareFormatDuration(seconds, true, true);
}

export function localeAwareFormatDuration(seconds: number, firstOnly = false, short = false): string {
  const formatDurationByLang = {
    fr: {
      halfAMinute: '30s',
      lessThanXSeconds: '< {{count}}s',
      lessThanXMinutes: '< {{count}} min',
      xSeconds: '{{count}}s',
      xMinutes: '{{count}} min',
      xHours: '{{count}} h',
      aboutXHours: '{{count}} h',
      xDays: '{{count}} j',
      aboutXWeeks: '{{count}} sem',
      xWeeks: '{{count}} sem',
      aboutXMonths: '{{count}} mois',
      xMonths: '{{count}} mois',
      aboutXYears: '{{count}}a',
      xYears: '{{count}}a',
      overXYears: '> {{count}}a',
      almostXYears: '{{count}}a',
    },
    en: {
      halfAMinute: '30s',
      lessThanXSeconds: '< {{count}}s',
      lessThanXMinutes: '< {{count}}m',
      xSeconds: '{{count}}s',
      xMinutes: '{{count}}m',
      xHours: '{{count}}h',
      aboutXHours: '{{count}}h',
      xDays: '{{count}}d',
      aboutXWeeks: '{{count}}w',
      xWeeks: '{{count}}w',
      aboutXMonths: '{{count}}mo',
      xMonths: '{{count}}mo',
      aboutXYears: '{{count}}y',
      xYears: '{{count}}y',
      overXYears: '> {{count}}y',
      almostXYears: '{{count}}y',
    },
    es: {
      halfAMinute: '30s',
      lessThanXSeconds: '< {{count}}s',
      lessThanXMinutes: '< {{count}} min',
      xSeconds: '{{count}}s',
      xMinutes: '{{count}} min',
      xHours: '{{count}}h',
      aboutXHours: '{{count}}h',
      xDays: '{{count}}d',
      aboutXWeeks: '{{count}}w',
      xWeeks: '{{count}}w',
      aboutXMonths: '{{count}} mes',
      xMonths: '{{count}} mes',
      aboutXYears: '{{count}}a',
      xYears: '{{count}}a',
      overXYears: '> {{count}}a',
      almostXYears: '{{count}}a',
    },
  };
  const shortLocale = {
    formatDistance: (
      token:
        | 'halfAMinute'
        | 'lessThanXSeconds'
        | 'lessThanXMinutes'
        | 'xSeconds'
        | 'xMinutes'
        | 'xHours'
        | 'aboutXHours'
        | 'xDays'
        | 'aboutXWeeks'
        | 'xWeeks'
        | 'aboutXMonths'
        | 'xMonths'
        | 'aboutXYears'
        | 'xYears'
        | 'overXYears'
        | 'almostXYears',
      count: number,
    ) => formatDurationByLang[getI18nLanguage()][token].replace('{{count}}', count.toString()),
  };
  const output = formatDuration(intervalToDuration({ start: new Date(0), end: new Date(seconds * 1000) }), {
    locale: short ? shortLocale : getLocale(),
    delimiter: '|',
    format: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
  }).split('|');
  return firstOnly ? output[0]! : output.join(' ');
}

export function localeAwareFormat(date: number | Date, pattern: string) {
  return format(date, pattern, { locale: getLocale() });
}

/**
 * Returns an array of 1-indexed days in the month
 * matching the provided date.
 */
export function getDaysInMonthAsArray(date: number | Date): Date[] {
  return Array.from({ length: getDaysInMonth(date) }, (_, i) => startOfDay(setDate(date, i + 1)));
}

/**
 * Takes in an arbitrary number of days and rounds it
 * to the next whole week.
 *
 * @example
 * roundToNextWholeWeek(31) // 35 (7 * 5)
 */
export function roundToNextWholeWeek(days: number) {
  if (days <= 0) throw new Error(`Expected positive integer, received ${days.toString()}`);
  return Math.ceil(days / 7) * 7;
}

export {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInDays,
  formatRelative,
  getDay,
  getDaysInMonth,
  isAfter,
  isBefore,
  isDate,
  isPast,
  isSameDay,
  isWithinInterval,
  max,
  nextFriday,
  nextMonday,
  nextSaturday,
  nextSunday,
  nextThursday,
  nextTuesday,
  nextWednesday,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth,
  subDays,
  subMilliseconds,
  subMonths,
} from 'date-fns';
